/* eslint-disable class-methods-use-this */
import { observable, makeObservable, action } from 'mobx';
import { setServer } from '../service/jiraOPService';
import config from '../config';
import Request from '../service/request';

const REST_URL = config.jiraAPI;
const REST3_URL = config.jiraAPI3 || '/rest/api/latest';

const getAP = () => {
  if (!window.AP || !window.AP.context || !window.AP.user) setServer();
  return window.AP;
};

class JiraStore {
  constructor() {
    if (window?.AP?.theming) window.AP.theming.initializeTheming();
    const schemaStr = (window.testomatioConfig && window.testomatioConfig.backendUrlSchema && (window.testomatioConfig.backendUrlSchema === 'http://')) ? 'http://' : 'https://';
    this.testomatioURL = (window.testomatioConfig && window.testomatioConfig.backendUrl) ? (schemaStr + window.testomatioConfig.backendUrl) : undefined;

    this.toast = null;
    this.branch = null;
    this.branches = [];
    this.issue = null;
    this.issueName = '';
    this.issues = null;
    this.suites = null;
    this.jiraId = null;
    this.projects = null;
    this.currentUser = null;
    this.childIssues = [];
    this.currentIssue = null;
    this.subscription = true;
    this.currentProject = null;
    this.associatedItems = null;
    this.associatedTests = null;
    this.associatedSuites = null;
    this.associatedRuns = null;
    this.associatedPlans = null;
    this.testomatioProject = null;
    this.testomatioToken = null;
    this.testomatioTokenVerified = null;
    this.testomatioLoadedData = {};
    this.isLoading = false;
    this.runDetails = null;
    this.showAllProjects = false;
    this.associatedProject = false;
    this.loadedSubscriptions = {};
    this.inflightChecks = {}
    this.searchApiMode = null;

    makeObservable(this, {
      // state
      toast: observable,
      branch: observable,
      branches: observable,
      issue: observable,
      issueName: observable,
      issues: observable,
      suites: observable,
      jiraId: observable,
      projects: observable,
      childIssues: observable,
      currentUser: observable,
      currentIssue: observable,
      // testomatioURL: observable,
      currentProject: observable,
      associatedTests: observable,
      subscription: observable,
      testomatioToken: observable,
      testomatioTokenVerified: observable,
      testomatioLoadedData: observable,
      associatedSuites: observable,
      testomatioProject: observable,
      showAllProjects: observable,
      // actions
      getIssue: action,
      setCurrentIssue: action,
      setBranch: action,
      setJiraId: action,
      getJiraId: action,
      getTestDetail: action,
      getIssueTypes: action,
      getCurrentUser: action,
      getSuiteDetail: action,
      getChildIssues: action,
      getCurrentIssue: action,
      getCurrentProject: action,
      getAssociatedTests: action,
      getIssuesWithTests: action,
      getTestomatioToken: action,
      getAssociatedSuites: action,
      getTestomatioProject: action,
      getTestDetailForIssue: action,
      getNavigateToIssuePage: action,
      getSuiteDetailForIssue: action,
      addBranch: action,
      getIssuesBySprint: action,
      getSprint: action,
      setAssociatedProject: action,
      itemsFromJiraProperties: action,
      searchApiMode: observable,
    });
  }

  async testomatioRequest(url, options = {}, prefix = this.apiPrefix) {
    if (!this.testomatioToken) return;

    const request = new Request(this.testomatioToken);
    if (this.branch) request.setBranch(this.branch);
    try {
      const resp = await request.send(prefix + url, options);
      if (options.success) {
        this.toast = {
          type: 'success',
          message: options.success,
        };
      }
      return resp;
    } catch (err) {
      this.toast = {
        type: 'error',
        message: err.message,
      };
      if (options.showError) throw err;
    }
  }

  async loadShowAllProjectsOrNoProjects() {
    if (!this.projects) {
      await this.getTestomatioProjects();
      if (this.projects.length === 0) this.showAllProjects = true;
    }
    return this.showAllProjectsOrNoProjects;
  }

  get showAllProjectsOrNoProjects() {
    if (this.projects === null) return true;
    return (this.showAllProjects || (this.projects.length === 0));
  }

  get apiPrefix() {
    return `/api/${this.testomatioProject.slug}`;
  }

  get projectUrl() {
    return `${this.testomatioURL}/projects/${this.testomatioProject.slug}`;
  }

  get isGherkin() {
    return this.testomatioProject?.lang === 'gherkin';
  }

  get branchId() {
    return (this.branch && this.branch.id) ? this.branch.id : config.mainBranchSlug;
  }

  get theme() {
    const htmlElement = document.documentElement;
    return htmlElement?.getAttribute('data-color-mode') || 'light';
  }

  itemsFromJiraProperties(properties) {
    if (!properties) return [];
    return properties.keys
      .filter(x => x.key)
      .map(x => {
        const keyArr = x.key.split('.');
        return {
          slug: keyArr[0],
          type: keyArr[2],
          id: keyArr[3],
          branch: keyArr[4] || null,
        }
      });
  }

  async setCurrentIssue(issueId) {
    this.currentIssue = issueId;
    if (issueId) {
      const issue = await this.getIssue(issueId);
      this.issueName = issue.fields.summary;
      this.jiraId = issue.key;
    } else {
      this.jiraId = null;
    }
  }

  async setBranch(branch) {
    if (!branch) {
      this.branch = null;
      return;
    }
    const resp = await this.testomatioRequest(`/branches/${branch.id}`, {
      method: 'GET',
    });
    if (!resp) {
      this.toast = { type: 'error', message: 'Branch was not found' };
      return;
    }
    this.branch = branch;
    this.addBranch(branch);
  }

  getCurrentIssue() {
    this.isLoading = true;
    return new Promise((res, rej) => {
      getAP().context.getContext((response) => {
        this.isLoading = false;
        if (response && response.jira && response.jira.issue) {
          this.currentIssue = response.jira.issue.id;
          this.jiraId = response.jira.issue.key;
          res(this.currentIssue);
        } else {
          this.toast = {
            type: 'error',
            message: 'Not in the issue page',
          };
          rej();
        }
      });
    });
  }

  getIssue(key) {
    return new Promise((res) => {
      getAP().request(`${REST_URL}/issue/${key}`, {
        success: async (responseText) => {
          const response = JSON.parse(responseText);
          if (response.id) {
            res(response);
          } else {
            this.toast = {
              type: 'error',
              message: 'No issue found',
            };
            res({});
          }
        },
        error: () => res({}),
      });
    });
  }

  getChildIssues(key) {
    return new Promise((res) => {
      this._searchJql(`"epic link"=${key}`, 100, { startAt: 0 }).then((response) => {
        if (response && response.issues && response.issues.length > 0) {
          this.childIssues = response.issues;
          res(this.childIssues);
        } else if (response && response.issues && response.issues.length === 0) {
          this._searchJql(`parent=${key}`, 100, { startAt: 0 }).then((resp) => {
            if (resp && resp.issues) res(resp.issues);
            else res([]);
          }).catch(() => res([]));
        } else {
          res([]);
        }
      }).catch(() => res([]));
    });
  }

  async loadLinkedItems(modelName = 'test', issueId = this.jiraId, params = {}) {
    const showAllProjs = await this.loadShowAllProjectsOrNoProjects();
    const slug = (showAllProjs) ? '' : this.testomatioProject.slug;
    const branchSlug = (this.branch) ? this.branch.id : '';
    const fields = (params.additionalFields) ? params.additionalFields.join(',') : '';
    const per_page = (params.per_page) ? params.per_page : '';
    if (!this.isUserAuthorized()) return;

    const url = `/user_projects/jiraprojects/${modelName}s?key=${this.currentProject?.key}&issue=${issueId}&slug=${slug}&branch=${branchSlug}&fields=${fields}&per_page=${per_page}`;
    const resp = await this.testomatioRequest(url, { showError: true }, '/api');
    // resp.data.forEach(item => { if (item.branch) this.addBranch(item.branch); });
    return resp.data;
  }

  getAssociatedTests(issueId, slug = this.testomatioProject.slug) {
    return new Promise((res) => {
      getAP().request(`${REST3_URL}/issue/${issueId}/properties`, {
        success: (responseText) => {
          const response = JSON.parse(responseText);
          this.associatedItems = response;
          const resultItems = this.getItemsListFromJiraPropertiesResponse('tests', slug, response);
          res(resultItems);
        },
        error: () => {
          res([]);
        },
      });
    });
  }

  setAssociatedProject() {
    if (this.associatedProject || !this.projects || this.projects?.length === 1) return;
    this.associatedProject = true;
    const byProject = {};
    this.associatedItems.keys.filter(x => x.key).forEach(k => {
      this.projects.forEach(p => {
        const keyPrefix = `${p.slug}.testomatio.tests.`;
        if (k.key.startsWith(keyPrefix)) {
          byProject[p.slug] = p;
        }
      });
    });
    if (Object.keys(byProject).length === 1) {
      const [project] = Object.values(byProject);
      this.setTestomatioProject(project);
    }
  }

  // Loading array of issue items from issue properties list
  // In properties list item key stored as /issue/${issueId}/properties/${projectSlug}.testomatio.tests.#{test_uid}.#{branch_slug}
  // So we filter properties by key_prefix, and choose linked items only for chosen branch_slug
  getItemsListFromJiraPropertiesResponse(model_name, projectSlug, response) {
    const result = [];
    const branchSlug = (this.branch) ? this.branch.id : config.mainBranchSlug;
    if (!(response.keys)) return result;
    const keyPrefix = `${projectSlug}.testomatio.${model_name}.`;
    response.keys.filter(x => x.key).forEach(prop => {
      if (prop.key.startsWith(keyPrefix)) {
        const itemKey = prop.key.slice(keyPrefix.length);
        const keyArr = itemKey.split('.').filter(x => x.length);
        const keyBranch = (keyArr.length > 1) ? keyArr[1] : config.mainBranchSlug;
        const itemUid = keyArr[0];
        if ((itemUid.length > 0) && ((keyBranch === config.mainBranchSlug) || (keyBranch === branchSlug))) {
          // Here we looking for best offer - if we have record in the branch_slug - we must take this record
          // If we do not found property with branch_slug - we need take record from Main branch
          // START [SearchBestBranchOffer]
          let alreadyFound = false;
          result.forEach(item => {
            if (item.id === itemUid) {
              alreadyFound = true;
              if (keyBranch === branchSlug) {
                item.branch = keyBranch;
                item.property_url = prop.self;
              }
            }
          });
          if (!(alreadyFound)) {
            result.push({
              id: itemUid, branch: keyBranch, branchSlug, property_url: prop.self,
            });
          }
          // END [SearchBestBranchOffer]
        }
      }
    });
    return result;
  }

  async getAssociatedSuites(issueId = this.currentIssue, slug = this.testomatioProject.slug) {
    if (!issueId) return Promise.resolve(null);
    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/issue/${issueId}/properties`,
        {
          success: (responseText) => {
            const response = JSON.parse(responseText);
            const resultItems = this.getItemsListFromJiraPropertiesResponse('suites', slug, response);
            this.associatedSuites = resultItems;
            res(resultItems);
          },
          error: () => {
            this.associatedSuites = [];
            res([]);
          },
        },
      );
    });
  }

  getCurrentProject() {
    if (this.currentProject?.key) return;
    return new Promise((res) => {
      getAP().context.getContext((response) => {
        if (response && response.jira && response.jira.project) {
          console.log(config.appPrefix, "Loaded project context:", response);
          let { project } = response.jira;
          if (!response.jira.project.key) {
            // in jira server project may not be returned when issue is opened from Search screen
            const key = response.jira.issue.key.split('-')[0];
            project = { key };
          }
          this.currentProject = project;
          res(project);
        } else {
          this.toast = {
            type: 'error',
            message: 'Not in the issue page',
          };
        }
        res(null);
      });
    });
  }

  isUserAuthorized() {
    return (this.testomatioToken && this.testomatioTokenVerified && (this.testomatioToken === this.testomatioTokenVerified));
  }

  async loadSettingsFromTestomatio(showError = false) {
    await this.getCurrentProject();
    const url = `/user_projects/jiraprojects?key=${this.currentProject?.key}`;
    const resp = await this.testomatioRequest(url, { showError}, '/api');
    if (!(this.testomatioTokenVerified && this.testomatioTokenVerified === this.testomatioToken)) {
      this.testomatioTokenVerified = this.testomatioToken;
    }
    this.testomatioLoadedData = (resp) ? { ...resp } : { loadFailed: true };
    this.projects = this.testomatioLoadedData.linked_projects || [];
    if (this.projects.length > 0 && !this.testomatioProject) {
      this.getTestomatioProject();
    }
  }

  async getTestomatioProjects() {
    if (!this.currentProject) return;

    if (this.testomatioToken) { // All logined in Testomat users will work with the fresh data from Testomat.
      await this.loadSettingsFromTestomatio();
      if (this.testomatioLoadedData.user) return;
    } else {
      this.projects = [];
      this.testomatioTokenVerified = null;
      if (this.testomatioLoadedData.user) this.testomatioLoadedData = {};
    }

    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/project/${this.currentProject?.key}/properties/testomatio.projects`,
        {
          success: (responseText) => {
            const response = JSON.parse(responseText);
            if (response.value) {
              this.projects = response.value;
            } else {
              this.toast = {
                type: 'error',
                message: 'No projects available',
              };
            }
            res(null);
          },
          error: (err) => {
            this.toast = {
              type: 'error',
              message: 'No projects available',
            };
            console.error(err); // eslint-disable-line
            res(null);
          },
        },
      );
    });
  }

  async getTestomatioProject() {
    if (!this.projects) {
      await this.getTestomatioProjects();
    }

    if (!this.currentProject || !this.projects) return;

    const project = this.projects[0];
    if (!project?.slug) return;

    const projectSlug = localStorage.getItem(`${this.currentProject.key}.project`);
    if (projectSlug) {
      const proj = this.projects.filter(p => p.slug === projectSlug)[0];
      if (proj) {
        this.testomatioProject = proj;
        return proj;
      }
    }
    this.setTestomatioProject(project);
    return this.testomatioProject;
  }

  setTestomatioProject(project) {
    localStorage.setItem(`${this.currentProject.key}.project`, project.slug);
    this.testomatioProject = project;
  }

  async checkProjectAccess() {
    await this.getCurrentUser();
    if (!this.currentUser) return false;
    const uid = this.currentUser.atlassianAccountId || this.currentUser.username;
    const slug = this.testomatioProject.slug;
    const key = `checkProjectAccess:${uid}:${slug}`;

    if (this.loadedSubscriptions[key]) {
      this.subscription = this.loadedSubscriptions[key];
      return true;
    }

    // If there is already an ongoing request for this uid/slug, wait for it.
    if (this.inflightChecks[key]) {
      console.log(config.appPrefix, 'Waiting for in-flight checkProjectAccess for key', key);
      return this.inflightChecks[key];
    }

    const request = new Request(this.testomatioToken);
    this.inflightChecks[key] = (async () => {
      try {
        const res = await request.send(`/api/projects/${slug}`, { method: 'GET' });
        const subscription = res.data.attributes.subscription;
        this.loadedSubscriptions[key] = subscription;
        this.subscription = subscription;
        return true;
      } catch (e) {
        return false;
      } finally {
        // We clean it up after completion so that we can request it again the next time we call it.
        delete this.inflightChecks[key];
      }
    })();

    return this.inflightChecks[key];
  }

  addBranch(branch) {
    if (!this.branches.map(b => b.id).filter(bs => bs === branch.id).length) {
      this.branches.push(branch);
    }
  }

  async getTestDetail(testItem, issueId = null) {
    const issue = issueId || this.currentIssue;
    const branchSlug = testItem.branch_slug || config.mainBranchSlug;
    let currentBranchPropUrl = testItem.property_url;
    let mainBranchPropUrl = '';
    if (!(currentBranchPropUrl)) {
      currentBranchPropUrl = `${REST_URL}/issue/${issue}/properties/${this.testomatioProject.slug}.testomatio.tests.${testItem.id}`;
      if (!(branchSlug === config.mainBranchSlug)) {
        mainBranchPropUrl = currentBranchPropUrl;
        currentBranchPropUrl = `${mainBranchPropUrl}.${branchSlug}`;
      }
    }
    return new Promise((res) => {
      getAP().request(currentBranchPropUrl, {
        success: (currentBranchResponseText) => {
          const currentBranchResponse = JSON.parse(currentBranchResponseText);
          if (currentBranchResponse.value) {
            currentBranchResponse.value.id = testItem.id;
            currentBranchResponse.value.branch_slug = branchSlug;
            if (currentBranchResponse.value.branch) this.addBranch(currentBranchResponse.value.branch);
            res(currentBranchResponse.value);
          } else {
            if ((mainBranchPropUrl.length > 0) && (currentBranchResponse.errorMessages) && (currentBranchResponse.errorMessages[0].match(/^The property .* does not exist/))) {
              getAP().request(mainBranchPropUrl, {
                success: (mainBranchResponseText) => {
                  const mainBranchResponse = JSON.parse(mainBranchResponseText);
                  if (mainBranchResponse.value) {
                    mainBranchResponse.value.id = testItem.id;
                    mainBranchResponse.value.branch_slug = branchSlug;
                    if (mainBranchResponse.value.branch) this.addBranch(mainBranchResponse.value.branch);
                    res(mainBranchResponse.value);
                  } else {
                    res({ id: testItem.id, branch_slug: branchSlug });
                  }
                },
                error: () => {
                  res({ id: testItem.id, branch_slug: branchSlug });
                },
              });
            }
            res({ id: testItem.id });
          }
        },
        error: () => {
          if (mainBranchPropUrl.length > 0) {
            getAP().request(mainBranchPropUrl, {
              success: (mainBranchResponseText2) => {
                const mainBranchResponse2 = JSON.parse(mainBranchResponseText2);
                if (mainBranchResponse2.value) {
                  mainBranchResponse2.value.id = testItem.id;
                  mainBranchResponse2.value.branch_slug = branchSlug;
                  if (mainBranchResponse2.value.branch) this.addBranch(mainBranchResponse2.value.branch);
                  res(mainBranchResponse2.value);
                } else res({ id: testItem.id, branch_slug: branchSlug });
              },
              error: () => {
                res({ id: testItem.id, branch_slug: branchSlug });
              },
            });
          } else {
            res({ id: testItem.id, branch_slug: branchSlug });
          }
        },
      });
    });
  }

  async getSuiteDetail(suiteItem, issueId = null) {
    const issue = issueId || this.currentIssue;
    const branchSlug = suiteItem.branch_slug || config.mainBranchSlug;
    let currentBranchPropUrl = suiteItem.property_url;
    let mainBranchPropUrl = '';
    if (!(currentBranchPropUrl)) {
      currentBranchPropUrl = `${REST_URL}/issue/${issue}/properties/${this.testomatioProject.slug}.testomatio.suites.${suiteItem.id}`;
      if (!(branchSlug === config.mainBranchSlug)) {
        mainBranchPropUrl = currentBranchPropUrl;
        currentBranchPropUrl = `${mainBranchPropUrl}.${branchSlug}`;
      }
    }
    return new Promise((res) => {
      getAP().request(currentBranchPropUrl, {
        success: (currentBranchResponseText) => {
          const currentBranchResponse = JSON.parse(currentBranchResponseText);
          if (currentBranchResponse.value) {
            currentBranchResponse.value.id = suiteItem.id;
            currentBranchResponse.value.branch_slug = branchSlug;
            if (currentBranchResponse.value.branch) this.addBranch(currentBranchResponse.value.branch);
            res(currentBranchResponse.value);
          } else {
            if ((mainBranchPropUrl.length > 0) && (currentBranchResponse.errorMessages) && (currentBranchResponse.errorMessages[0].match(/^The property .* does not exist/))) {
              getAP().request(mainBranchPropUrl, {
                success: (mainBranchResponseText) => {
                  const mainBranchResponse = JSON.parse(mainBranchResponseText);
                  if (mainBranchResponse.value) {
                    mainBranchResponse.value.id = suiteItem.id;
                    mainBranchResponse.value.branch_slug = branchSlug;
                    if (mainBranchResponse.value.branch) this.addBranch(mainBranchResponse.value.branch);
                    res(mainBranchResponse.value);
                  } else {
                    res({ id: suiteItem.id, branch_slug: branchSlug });
                  }
                },
                error: () => {
                  res({ id: suiteItem.id, branch_slug: branchSlug });
                },
              });
            }
            res({ id: suiteItem.id });
          }
        },
        error: () => {
          if (mainBranchPropUrl.length > 0) {
            getAP().request(mainBranchPropUrl, {
              success: (mainBranchResponseText2) => {
                const mainBranchResponse2 = JSON.parse(mainBranchResponseText2);
                if (mainBranchResponse2.value) {
                  mainBranchResponse2.value.id = suiteItem.id;
                  mainBranchResponse2.value.branch_slug = branchSlug;
                  if (mainBranchResponse2.value.branch) this.addBranch(mainBranchResponse2.value.branch);
                  res(mainBranchResponse2.value);
                } else res({ id: suiteItem.id, branch_slug: branchSlug });
              },
              error: () => {
                res({ id: suiteItem.id, branch_slug: branchSlug });
              },
            });
          } else {
            res({ id: suiteItem.id, branch_slug: branchSlug });
          }
        },
      });
    });
  }

  async getTestDetailForIssue(issueId, slug = this.testomatioProject.slug) {
    const tests = await this.getAssociatedTests(issueId, slug);
    if (tests) {
      const testDetailsPromise = tests.map((testItem) => this.getTestDetail(testItem, issueId));
      return (await Promise.allSettled(testDetailsPromise)).map((p) => {
        if (!p.value && p.id) {
          return {
            id: p.id,
            title: 'Synchronizing... Reload this tab',
            type: 'test',
            status: [],
          };
        }
        if (!p.value && !p.id) {
          return {
            id: '',
            title: 'Not loaded.',
            type: 'test',
            status: [],
          };
        }
        return p.value;
      });
    }
    return [];
  }

  paginationMetaFromJiraApiPages(responseData) {
    if (!responseData || responseData.error) {
      return { total: 0, hasPrev: false, hasNext: false, nextPageToken: null };
    }

    if (responseData.nextPageToken !== undefined || responseData.isLast !== undefined) {
      const total = responseData.totalIssueCount || responseData.total || (responseData.isLast ? (responseData.issues || []).length : 0);
      return {
        total,
        hasPrev: false,
        hasNext: responseData.isLast === false,
        nextPageToken: responseData.nextPageToken || null,
      };
    }

    const { maxResults, total, startAt } = responseData;
    if (!maxResults || total == null || startAt == null) {
      return { total: total || 0, hasPrev: false, hasNext: false, nextPageToken: null };
    }

    return {
      total: total || 0,
      hasPrev: startAt > 0,
      hasNext: (startAt + maxResults) < total,
      nextPageToken: null,
    };
  }

  async _searchJqlCloud(jql, maxResults, nextPageToken) {
    const requestBody = { jql, maxResults, fields: ['*all'] };
    if (nextPageToken) requestBody.nextPageToken = nextPageToken;

    return new Promise((resolve) => {
      getAP().request({
        url: '/rest/api/3/search/jql',
        type: 'POST',
        contentType: 'application/json',
        data: JSON.stringify(requestBody),
        success: (responseText) => {
          try {
            resolve(JSON.parse(responseText));
          } catch (e) {
            resolve({ error: true, message: 'Failed to parse response' });
          }
        },
        error: (xhr, statusText, errorThrown) => {
          const status = (typeof xhr === 'object' && xhr?.status) ? xhr.status : 0;
          const responseText = (typeof xhr === 'object' && xhr?.responseText) ? xhr.responseText : String(errorThrown || xhr || '');
          resolve({
            error: true,
            status,
            statusText,
            message: responseText || errorThrown || 'Request failed',
            isApiUnavailable: [404, 405, 410].includes(status),
          });
        },
      });
    });
  }

  async _searchJqlDc(jql, maxResults, startAt) {
    const encodedJql = encodeURIComponent(jql);
    const url = `${REST3_URL}/search?jql=${encodedJql}&startAt=${startAt}&maxResults=${maxResults}`;

    return new Promise((resolve) => {
      getAP().request(url, {
        success: (responseText) => {
          try {
            resolve(JSON.parse(responseText));
          } catch (e) {
            resolve({ error: true, message: 'Failed to parse response' });
          }
        },
        error: (xhr, statusText, errorThrown) => {
          resolve({
            error: true,
            status: (typeof xhr === 'object' && xhr?.status) ? xhr.status : 0,
            statusText,
            message: (typeof xhr === 'object' && xhr?.responseText) ? xhr.responseText : String(errorThrown || xhr || ''),
          });
        },
      });
    });
  }

  async _searchJql(jql, maxResults, pageParams = {}) {
    if (!this.searchApiMode) {
      const cloudResult = await this._searchJqlCloud(jql, maxResults, pageParams.nextPageToken || null);
      if (!cloudResult.error) {
        this.searchApiMode = 'cloud';
        return cloudResult;
      }
      if (cloudResult.isApiUnavailable) {
        this.searchApiMode = 'dc';
      } else {
        this.searchApiMode = 'cloud';
        return cloudResult;
      }
    }

    if (this.searchApiMode === 'cloud') {
      return this._searchJqlCloud(jql, maxResults, pageParams.nextPageToken || null);
    }

    return this._searchJqlDc(jql, maxResults, pageParams.startAt || 0);
  }

  async getSuiteDetailForIssue(issueId) {
    const suites = await this.getAssociatedSuites(issueId);
    if (suites) {
      const detailsPromise = suites.map((suitePropInfo) => this.getSuiteDetail(suitePropInfo, issueId));
      return (await Promise.allSettled(detailsPromise)).map((p) => {
        if (!p.value && p.id) {
          return {
            id: p.id,
            title: 'Synchronizing... Reload this tab',
            type: 'suite',
            status: [],
          };
        }
        if (!p.value && !p.id) {
          return {
            id: '',
            title: 'Not loaded.',
            type: 'suite',
            status: [],
          };
        }
        return p.value;
      });
    }
    return [];
  }

  async getAllIssues(filter = {}, params = {}) {
    let jqlAnds = []
    if (filter.project) jqlAnds.push(`project in (${filter.project})`);
    if (filter.sprint) jqlAnds.push(`sprint in (${filter.sprint})`);
    if (filter.issuetype) jqlAnds.push(`issuetype in (${filter.issuetype})`);
    if (filter.jql && filter.jql.length > 0) jqlAnds.push(`(${filter.jql})`);
    const jql = jqlAnds.join(' AND ');

    return this._searchJql(jql, 100, params);
  }

  async getIssuesBySprint(sprintId) {
    const jql = `sprint=${sprintId}`;
    let allIssues = [];
    let pageParams = { startAt: 0 };

    const firstPage = await this._searchJql(jql, 100, pageParams);
    if (!firstPage || !firstPage.issues) return null;
    allIssues = [...firstPage.issues];

    const meta = this.paginationMetaFromJiraApiPages(firstPage);
    if (meta.hasNext && this.searchApiMode === 'cloud' && firstPage.nextPageToken) {
      pageParams = { nextPageToken: firstPage.nextPageToken };
      for (let i = 0; i < 1000; i++) {
        const nextPage = await this._searchJql(jql, 100, pageParams);
        if (!nextPage || !nextPage.issues || nextPage.issues.length === 0) break;
        allIssues = [...allIssues, ...nextPage.issues];
        if (nextPage.isLast !== false) break;
        if (nextPage.nextPageToken) {
          pageParams = { nextPageToken: nextPage.nextPageToken };
        } else {
          break;
        }
      }
    } else if (meta.hasNext && this.searchApiMode === 'dc') {
      let startAt = 100;
      for (let i = 0; i < 1000; i++) {
        pageParams = { startAt };
        const nextPage = await this._searchJql(jql, 100, pageParams);
        if (!nextPage || !nextPage.issues || nextPage.issues.length === 0) break;
        allIssues = [...allIssues, ...nextPage.issues];
        if (nextPage.issues.length < 100) break;
        startAt += nextPage.issues.length;
      }
    }

    return allIssues;
  }

  async getIssuesWithTests(filter, params) {
    const foundData = await this.getAllIssues(filter, params);

    if (foundData.error) {
      this.toast = { type: 'error', message: 'Error found: ' + foundData.message };
      return foundData;
    }

    if (foundData.errorMessages && foundData.errorMessages.length) {
      this.toast = { type: 'error', message: 'Error: ' + foundData.errorMessages.join("\n") };
      return foundData;
    }

    if (!(foundData.issues && foundData.issues.length)) {
      if (filter.jql && filter.jql.length) {
        this.toast = { type: 'error', message: 'No results matches criteria' };
      }
      return foundData;
    }

    const idsCommed1 = foundData.issues.map(x => x.key).join(',');
    const actualTests = await this.loadLinkedItems('test', idsCommed1, { additionalFields: ['jira_ids'], per_page: 50_000 });

    foundData.issues = foundData.issues.map(issue => {
      const tests = actualTests.filter(x => (x.jira_ids || []).includes(issue.key));
      const issueProjectsCount = new Set(tests.map(x => x.project)).size;
      return { ...issue, tests, issueProjectsCount };
    }).sort((a, b) => {
      if (a.tests.length < b.tests.length) {
        return 1;
      }
      if (a.tests.length > b.tests.length) {
        return -1;
      }
      return 0;
    });

    return foundData;
  }

  async getIssueTypes(projectId) {
    return new Promise((res) => {
      getAP().request(`${REST_URL}/project/${projectId}`, {
        success: (responseText) => {
          const resp = JSON.parse(responseText);
          if (resp.issueTypes) {
            res(resp.issueTypes);
          }
        },
      });
    });
  }

  getNavigateToIssuePage(issueId) {
    getAP().navigator.go('site', {
      relativeUrl: `/browse/${issueId}`,
    });
  }

  getCurrentUser() {
    return new Promise((res) => {
      getAP().user.getCurrentUser((user) => {
        if (user) {
          this.currentUser = user;
        }
        res(null);
      });
    });
  }

  async getTestomatioToken() {
    await this.getCurrentUser();
    console.log(config.appPrefix, 'Loading auth token from cookie', { account: this.currentUser?.atlassianAccountId, name: this.currentUser?.username, url: this.testomatioURL });
    if (!this.currentUser) return null;

    const cookieToken = this.getPluginCookie(this.testomatCookieName(this.testomatioURL, 'jwt'));
    console.log(config.appPrefix, 'Loaded auth token from cookie:', cookieToken);
    if (cookieToken.length > 0) {
      try {
        const request = new Request(cookieToken);
        const data = await request.send('/api/verify', { method: 'GET' });
        if (data.message === 'ok') {
          console.log(config.appPrefix, 'Auth token from cookie has been verified');
          this.testomatioToken = cookieToken;
          return cookieToken;
        }
      } catch (e) {
        console.error(config.appPrefix, 'Failed to verify cookie auth token', e); // eslint-disable-line
      }
    }

    console.log(config.appPrefix, 'Loading auth token from property', { account: this.currentUser.atlassianAccountId, username: this.currentUser.username });
    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/user/properties/testomatio.token?accountId=${this.currentUser.atlassianAccountId}&username=${this.currentUser.username}`,
        {
          success: (responseText) => {
            console.log(config.appPrefix, 'Loading auth token from property response:', responseText);
            const response = JSON.parse(responseText);
            if (response.value && response.value.token) {
              this.testomatioToken = response.value.token;
              console.log(config.appPrefix, 'Auth token loaded from property success. Token:', this.testomatioToken);
            }
            res(this.testomatioToken);
          },
          error: () => {
            console.log(config.appPrefix, 'Loading auth token from property failed.');
            this.testomatioToken = null;
            res(null);
          },
        },
      );
    });
  }

  testomatCookieName(testomatDomain, name) {
    const account_id = this.getJiraAccountIdent();
    const result = `${account_id}_${testomatDomain}`.replaceAll(/[^A-Za-z0-9_]/ig, '_');
    return `testomat_${name}_${result}`;
  }

  getJiraAccountIdent() {
    const jiraUser = this.currentUser;
    if (!jiraUser) return null;
    const account_id = jiraUser.atlassianAccountId || jiraUser.username || 'noUser';
    return `${account_id}`;
  }

  setPluginCookie(cookieName, value) {
    const domainUri = new URL('/', window.location.href);
    const secure = (domainUri.protocol === 'https:') ? 'Secure;' : '';
    const domain = domainUri.host.split(':')[0];
    const sameSite = (secure) ? 'None' : 'Lax';
    const set_cookie_str = `${cookieName}=${value};Max-Age=257680000;domain=${domain};SameSite=${sameSite};${secure}`;
    document.cookie = `${set_cookie_str}path=/;`;
  }

  getPluginCookie(cookieName) {
    let result = '';
    const cookies = document.cookie.split(';');
    for (let i = 0; i < cookies.length; i++) {
      const cookie = cookies[i].trim();
      if (cookie.startsWith(`${cookieName}=`)) result = cookie.substring(cookieName.length + 1);
    }
    return result;
  }

  setJiraId(jiraId) {
    this.jiraId = jiraId;
  }

  getJiraId() {
    return this.jiraId;
  }

  getAssociatedPlans(issueId, slug = this.testomatioProject.slug) {
    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/issue/${issueId}/properties/${slug}.testomatio.plans`,
        {
          success: (responseText) => {
            const response = JSON.parse(responseText);
            if (response.value) {
              if (issueId === this.currentIssue) {
                this.associatedPlans = response.value;
              }
              return res(response.value);
            }
            res([]);
          },
          error: () => {
            res([]);
          },
        },
      );
    });
  }

  getAssociatedRuns(issueId, slug = this.testomatioProject.slug) {
    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/issue/${issueId}/properties/${slug}.testomatio.runs`,
        {
          success: (responseText) => {
            const response = JSON.parse(responseText);
            if (response.value) {
              if (issueId === this.currentIssue) {
                this.associatedRuns = response.value;
              }
              return res(response.value);
            }
            res([]);
          },
          error: () => {
            res([]);
          },
        },
      );
    });
  }

  async getRunDetails(runID, issueId = null, slug = this.testomatioProject.slug) {
    const issue = issueId || this.currentIssue;
    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/issue/${issue}/properties/${slug}.testomatio.runs.${runID}`,
        {
          success: (responseText) => {
            const response = JSON.parse(responseText);
            if (response.value) {
              response.value.id = runID;
              if (response.value.branch) this.addBranch(response.value.branch);
              res(response.value);
            } else res({ id: runID });
          },
          error: () => {
            res({ id: runID });
          },
        },
      );
    });
  }

  getTestsBySlug(issueId, slug) {
    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/issue/${issueId}/properties/${slug}.testomatio.tests`,
        {
          success: (responseText) => {
            const response = JSON.parse(responseText);
            if (response.value) {
              if (issueId === this.currentIssue) {
                this.associatedTests = response.value;
              }
              return res(response.value);
            }
            res([]);
          },
          error: () => {
            res([]);
          },
        },
      );
    });
  }

  getTestDetailsBySlug(testId, issueId = null, slug) {
    const issue = issueId || this.currentIssue;
    return new Promise((res) => {
      getAP().request(
        `${REST_URL}/issue/${issue}/properties/${slug}.testomatio.tests.${testId}`,
        {
          success: (responseText) => {
            const response = JSON.parse(responseText);
            if (response.value) {
              response.value.id = testId;
              if (response.value.branch) this.addBranch(response.value.branch);
              res(response.value);
            } else res({ id: testId });
          },
          error: () => {
            res({ id: testId });
          },
        },
      );
    });
  }

  async getAllTestsDetails(issueId, slug) {
    const tests = await this.getTestsBySlug(issueId, slug);
    if (tests) {
      const testDetailsPromise = tests.map((testId) => this.getTestDetailsBySlug(testId, issueId, slug));
      return (await Promise.allSettled(testDetailsPromise)).map((p) => {
        if (!p.value && p.id) {
          return {
            id: p.id,
            title: 'Synchronizing... Reload this tab',
            type: 'test',
            status: [],
          };
        }
        if (!p.value && !p.id) {
          return {
            id: '',
            title: 'Not loaded.',
            type: 'test',
            status: [],
          };
        }
        return p.value;
      });
    }
    return [];
  }

  getSprint(sprintId) {
    return new Promise((res) => {
      getAP().request(`/rest/agile/1.0/sprint/${sprintId}`, {
        success: async (responseText) => {
          const response = JSON.parse(responseText);
          if (response.id) {
            res(response);
          } else {
            this.toast = {
              type: 'error',
              message: 'No sprint found',
            };
            res({});
          }
        },
        error: () => res({}),
      });
    });
  }

  clearSessionSettings() {
    localStorage.setItem('page', 1);
  }
}

export default JiraStore;
